"use client"

import { useEffect, useState, useCallback, useRef } from "react"
import "./ChooseForensics.css"
import config from "./config"
import Bot from "./bot"
import { CircularProgressbar, buildStyles } from "react-circular-progressbar"
import "react-circular-progressbar/dist/styles.css"
import Modal from "./Modal"
import Header from "./Header"
import { useNavigate } from "react-router-dom"

const ChooseForensics = ({
  uploadedFiles,
  isLoggedIn,
  username,
  accessKey,
  remainingCredits,
  setRemainingCredits,
  userId,
  setUploadedFiles,
  jobRole,
  setAccessKey,
  setUsername,
  setIsLoggedIn,
  setFileLength,
  folder,
  driveLink,
  parseId,
  name,
  setIsRegisterModalOpen,
  isRegisterModalOpen,
  fileLength,
  fileD,
}) => {
  const [runId, setRunID] = useState(null)
  const [showForensicsModal, setShowForensicsModal] = useState(true)
  const [isAnimating, setIsAnimating] = useState(false)
  const [popupMessage, setPopupMessage] = useState("")
  const [showPopup, setShowPopup] = useState(false)
  const [hasLoggedOut, setHasLoggedOut] = useState(false)
  const [progress, setProgress] = useState(0)
  const [isParsingComplete, setIsParsingComplete] = useState(false)
  const [showNotificationPopup, setShowNotificationPopup] = useState(false)

  // Use a ref instead of state to track if JDM forensics has been called
  // This ensures immediate updates without waiting for re-renders
  const jdmForensicsCalledRef = useRef(false)

  // Use a ref to track if a parse status check is already in progress
  const isCheckingParseStatusRef = useRef(false)

  const navigate = useNavigate()

  const triggerPopup = (message) => {
    setPopupMessage(message)
    setShowPopup(true)
    setTimeout(() => {
      setShowPopup(false)
    }, 3000)
  }

  useEffect(() => {
    // Load Google Analytics
    const script1 = document.createElement("script")
    script1.src = "https://www.googletagmanager.com/gtag/js?id=G-V32YGHXF75"
    script1.async = true
    document.head.appendChild(script1)

    const script2 = document.createElement("script")
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-V32YGHXF75');
    `
    document.head.appendChild(script2)

    // Cleanup function
    return () => {
      document.head.removeChild(script1)
      document.head.removeChild(script2)
    }
  }, []) // Empty dependency array ensures this runs once on mount

  const handleBotLoad = (hubspot) => {
    // HubSpot chatbot loaded successfully
  }

  const checkCreditStatus = async (totalApiHits) => {
    try {
      const response = await fetch(`${config.AUTH_BASE_URL}/api/credit/check-status`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessKey}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: userId,
          product_name: "MAYAMAYA_LITE",
          total_api_hits: totalApiHits,
        }),
      })

      const creditData = await response.json()
      if (creditData.msg && creditData.msg.includes("Bad Authorization header") && !hasLoggedOut) {
        handleLogout()
        setHasLoggedOut(true)
      }
      if (response.ok && creditData.status === 200) {
        setRemainingCredits(creditData.data.credits)
        return creditData.data.credits
      } else {
        triggerPopup(creditData.message || "Error checking credit status")
        return false
      }
    } catch (error) {
      console.error("Error checking credit status:", error)
      return false
    }
  }

  const handleCloseNotificationPopup = () => {
    setShowNotificationPopup(false)
  }

  const handleLogout = () => {
    setIsLoggedIn(false)
    setUsername("")
    setAccessKey("")
    localStorage.removeItem("access_token")
    localStorage.removeItem("email")
    localStorage.removeItem("uploadedFiles")
    localStorage.removeItem("driveLink")
    window.location.href = "/"
  }

  const handleForensicsChoice = async (applyForensics) => {
    setShowForensicsModal(false)
    setIsAnimating(true)

    const storedEmail = localStorage.getItem("email")
    const numFiles = uploadedFiles.length
    if (fileLength < 1) {
      setFileLength(numFiles)
    }

    const availableCredits = await checkCreditStatus(numFiles)

    if (availableCredits < numFiles) {
      triggerPopup("Insufficient credits. Please recharge.")
      setIsAnimating(false) // Stop the animation
      return // Exit the function early
    }

    // First check parse status to determine if folder is already parsed
    try {
      const response = await fetch(
        `${config.DATASCIENCE_API_SUITE}/api/mayamaya-lite/folder/parse-status?parse_id=${parseId}`,
        {
          headers: {
            Authorization: `Bearer ${accessKey}`,
          },
        },
      )
      const data = await response.json()

      if (data.data.folder_parsing_status === "completed") {
        // If parsing is already complete, call JDM forensics immediately
        setProgress(100)
        setIsParsingComplete(true)
        callJdmForensicsApi()
      } else {
        // If parsing is not complete, start the progress animation
        startProgressAnimation(numFiles)

        // Start checking parse status with a 10-second delay
        setTimeout(() => {
          checkParseStatus()
        }, 10000)
      }
    } catch (error) {
      console.error("Error checking initial parse status:", error)
      // If there's an error, still start the animation and check status
      startProgressAnimation(numFiles)
      setTimeout(() => {
        checkParseStatus()
      }, 10000)
    }
  }

  const startProgressAnimation = (numFiles) => {
    const totalDuration = fileD * 2.1 * 1000
    const interval = 100
    let steps = (totalDuration * 0.9) / interval

    // Prevent division by zero or near-zero values
    if (!steps || steps <= 0) steps = 1

    const progressInterval = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress >= 90) {
          clearInterval(progressInterval)
          return 90
        }
        return prevProgress + 90 / steps
      })
    }, interval)
  }

  // Update the checkParseStatus function to use the ref and prevent concurrent calls
  const checkParseStatus = useCallback(async () => {
    // If already checking, don't start another check
    if (isCheckingParseStatusRef.current) {
      return
    }

    // Set the flag to indicate we're checking
    isCheckingParseStatusRef.current = true

    try {
      const response = await fetch(
        `${config.DATASCIENCE_API_SUITE}/api/mayamaya-lite/folder/parse-status?parse_id=${parseId}`,
        {
          headers: {
            Authorization: `Bearer ${accessKey}`,
          },
        },
      )
      const data = await response.json()

      if (data.data.folder_parsing_status === "completed") {
        setProgress(100)
        setIsParsingComplete(true)

        // Only call JDM forensics if it hasn't been called yet
        if (!jdmForensicsCalledRef.current) {
          callJdmForensicsApi()
        }
      } else {
        // Schedule the next check after 10 seconds
        setTimeout(() => {
          // Reset the checking flag before the next check
          isCheckingParseStatusRef.current = false
          checkParseStatus()
        }, 10000)
      }
    } catch (error) {
      console.error("Error checking parse status:", error)
      // Schedule the next check after 10 seconds even if there was an error
      setTimeout(() => {
        // Reset the checking flag before the next check
        isCheckingParseStatusRef.current = false
        checkParseStatus()
      }, 10000)
    } finally {
      // If we're not scheduling another check (because parsing is complete),
      // reset the checking flag
      if (isParsingComplete) {
        isCheckingParseStatusRef.current = false
      }
    }
  }, [parseId, accessKey])

  // Update the callJdmForensicsApi function to use the ref
  const callJdmForensicsApi = async () => {
    // Check again to make absolutely sure we don't call this twice
    if (jdmForensicsCalledRef.current) {
      console.log("JDM Forensics already called, skipping duplicate call")
      return
    }

    // Set the flag to prevent multiple calls - do this immediately
    jdmForensicsCalledRef.current = true
    console.log("Calling JDM Forensics API")

    const storedEmail = localStorage.getItem("email")

    const payload = {
      email: storedEmail,
      job_id: jobRole._id,
      folder_name: folder,
      forensics: true,
      jdm: true,
      user_id: userId,
    }

    try {
      const response = await fetch(`${config.DATASCIENCE_API_SUITE}/api/mayamaya-lite/jdm-forensics`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessKey}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      })

      if (response.ok) {
        const data = await response.json()

        if (data.status === 400) {
          setIsAnimating(false)
          triggerPopup(data.message)
          return
        }

        if (data.data && data.data.run_id) {
          setRunID(data.data.run_id)
        } else {
          console.error("Run ID not found in the response:", data)
          return
        }

        if (data.data.parsing === false) {
          setFileLength(0)
        }

        // Update remaining credits
        const numFiles = uploadedFiles.length
        const availableCredits = await checkCreditStatus(numFiles)
        setRemainingCredits(availableCredits - numFiles)

        // Now navigate to results page after getting the API response
        navigate(`/results?run_id=${data.data.run_id}&folder_name=${folder}&job_role=${jobRole.jobTitle}&load=true`)
      } else {
        console.error("Error sending data to the backend.")
      }
    } catch (error) {
      console.error("Error in API request:", error)
    }
  }

  const handleClosePopup = () => {
    setShowPopup(false)
    setPopupMessage("")
  }

  useEffect(() => {
    handleForensicsChoice(true)
  }, [])

  const [isModalOpen, setIsModalOpen] = useState(true)

  const handleModalClose = () => setIsModalOpen(false)

  const popupAnimationStyle = `
    @keyframes popupAnimation {
      0% {
        opacity: 0;
        transform: scale(0.8);
      }
      70% {
        opacity: 1;
        transform: scale(1.05);
      }
      100% {
        opacity: 1;
        transform: scale(1);
      }
    }
  `

  useEffect(() => {
    // Show notification popup after 2 seconds
    const timer = setTimeout(() => {
      setShowNotificationPopup(true)
    }, 2000)

    return () => clearTimeout(timer)
  }, [])

  return (
    <div>
      <style>{popupAnimationStyle}</style>
      <Header
        setIsLoggedIn={setIsLoggedIn}
        setUsername={setUsername}
        setAccessKey={setAccessKey}
        remainingCredits={remainingCredits}
        setIsRegisterModalOpen={setIsRegisterModalOpen}
        setIsModalOpen={setIsModalOpen}
        isModalOpen={isModalOpen}
        isRegisterModalOpen={isRegisterModalOpen}
        name={name}
      />
      <Modal isOpen={isModalOpen} onClose={handleModalClose}>
        <div>
          <div className="stepper-header">
            <p>SHORTLIST CANDIDATES</p>
          </div>

          <div className="step-progress-bar">
            <div style={{ display: "flex", justifyContent: "space-between", textAlign: "center" }}>
              <p style={{ width: "33.33%", color: "#777" }}>1. Create/Choose Job Role</p>
              <p style={{ width: "33.33%", color: "#777" }}>2. Upload Resumes</p>
              <p style={{ width: "33.33%", color: "#fff" }}>3. Create Folder</p>
            </div>
            <div className="progress-line" style={{ margin: "10px 0" }}>
              <div className="step-progress-fill" style={{ width: "33.33%", backgroundColor: "#777" }}></div>
              <div className="step-progress-fill" style={{ width: "33.33%", backgroundColor: "#777" }}></div>
              <div className="step-progress-fill" style={{ width: "33.33%", backgroundColor: "#fff" }}></div>
            </div>
          </div>

          {showPopup && (
            <div>
              <div className="overlay" onClick={handleClosePopup}></div>
              <div className="popup" style={{ textAlign: "center" }}>
                <button className="close-btn" onClick={handleClosePopup}>
                  ×
                </button>
                {popupMessage}
              </div>
            </div>
          )}

          <div
            style={{ padding: "20px", display: "flex", flexDirection: "column", alignItems: "center", height: "100vh" }}
          >
            <div style={{ width: 150, height: 150 }}>
              <svg style={{ height: 0 }}>
                <defs>
                  <linearGradient id="progressGradient" gradientTransform="rotate(73)">
                    <stop offset="12.05%" stopColor="#030507" />
                    <stop offset="88.83%" stopColor="#113e33" />
                  </linearGradient>
                </defs>
              </svg>
              <CircularProgressbar
                value={progress}
                text={`${Math.round(progress)}%`}
                styles={buildStyles({
                  pathColor: "url(#progressGradient)",
                  textColor: "#000",
                  trailColor: "#d6d6d6",
                  backgroundColor: "#f8f8f8",
                  textSize: "18px",
                  strokeLinecap: "square",
                })}
                strokeWidth={20}
                diameter={200}
              />
            </div>
            <p className="progress-text" style={{ marginTop: 30, fontSize: "1rem" }}>
              Validating & Analyzing Resumes...
            </p>
            <p className="progress-text" style={{ marginTop: 10, fontSize: "0.9rem" }}>
              Our AI is reviewing the resumes based on experience, education, and skills.{" "}
            </p>
            {showNotificationPopup && (
              <div
                className="numfiles-error-popup"
                style={{marginTop: "50px"}}
              >
                <button className="numfiles-error-close-btn" onClick={() => handleCloseNotificationPopup()}>
                  ×
                </button>
                <h3 style={{textAlign: "center"}}>Our AI is reviewing the resumes based on experience, education, and skills.{" "}</h3>
            
                <p className="progress-text" style={{ marginTop: 10, fontSize: "1.2rem", lineHeight: "1.5" }}>
                  You'll receive an email once results are ready. You can also check them{" "}
                  <span
                    style={{ textDecoration: "underline", cursor: "pointer", color: "#672924" }}
                    onClick={() => navigate("/history", { replace: true })}
                  >
                    here
                  </span>{" "}
                  when available.
                </p>
              </div>
            )}
          </div>

          <Bot onLoad={handleBotLoad} />
        </div>
      </Modal>
    </div>
  )
}

export default ChooseForensics
